import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { get } from "@/lib/api";
import { fmtTime } from "@/lib/format";
import { RunBadge } from "@/admin/ui";
import { useRefresh } from "@/admin/hooks";

const LEVEL = { success: "text-emerald-400", warning: "text-amber-300", error: "text-rose-400", info: "text-slate-300" };

export const RunLog = ({ log = [], running }) => (
  <ol className="font-mono text-[12px] leading-relaxed space-y-1.5 max-h-72 overflow-auto bg-ink p-4" data-testid="run-log">
    {log.map((l, i) => (
      <li key={i} className="flex gap-3">
        <span className="text-slate-500 shrink-0">{fmtTime(l.t)}</span>
        <span className={LEVEL[l.level] || LEVEL.info}>{l.msg}</span>
      </li>
    ))}
    {running && <li className="text-gold animate-pulse">▍ trabajando…</li>}
  </ol>
);

export const RunProgress = ({ runId, onOpen }) => {
  const navigate = useNavigate();
  const refresh = useRefresh();
  const { data: run } = useQuery({
    queryKey: ["run", runId],
    queryFn: () => get(`/admin/runs/${runId}`),
    enabled: !!runId,
    refetchInterval: (q) => (!q.state.data || q.state.data.status === "running" ? 2000 : false),
  });
  const done = run && run.status !== "running";
  useEffect(() => { if (done) refresh(); }, [done]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="run-progress">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm">
          {done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          <span data-testid="run-message">{done ? run.message : "La IA está trabajando. Suele tardar menos de un minuto por noticia."}</span>
        </div>
        {run && <RunBadge status={run.status} />}
      </div>
      <RunLog log={run?.log} running={!done} />
      {done && run.article_ids?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {run.article_ids.slice(0, 3).map((id, i) => (
            <Button key={id} onClick={() => { onOpen?.(); navigate(`/admin/noticias/${id}`); }} variant={i ? "outline" : "default"} data-testid={`run-open-article-${i}`}>
              Abrir noticia {run.article_ids.length > 1 ? i + 1 : ""}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export const RunDialog = ({ runId, onClose }) => (
  <Dialog open={!!runId} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-xl" data-testid="run-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Generando contenido</DialogTitle>
        <DialogDescription>Puedes cerrar esta ventana: el trabajo continúa y queda registrado en Actividad.</DialogDescription>
      </DialogHeader>
      {runId && <RunProgress runId={runId} onOpen={onClose} />}
    </DialogContent>
  </Dialog>
);
